import { ChevronRightIcon } from "lucide-react";
import type { ReactNode } from "react";
import { Link, matchPath, useLocation } from "react-router-dom";

import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/shared/components/primitives/collapsible";
import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuAction,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
} from "@/shared/components/primitives/sidebar";

export type TenkoNavMainSubItem = {
  to: string;
  path?: string;
  label: string;
  /** Optional trailing badge (e.g. a pending-count pill). Rendered right-aligned inside the item. */
  badge?: ReactNode;
};

export type TenkoNavMainSection = {
  id: string;
  title?: string;
  icon?: ReactNode;
  to?: string;
  path?: string;
  defaultOpen?: boolean;
  items?: TenkoNavMainSubItem[];
};

function isMatch(pattern: string | undefined, pathname: string): boolean {
  if (!pattern) return false;
  return Boolean(matchPath({ path: pattern, end: false }, pathname));
}

function TenkoNavMainGroupEntry({ group }: { group: TenkoNavMainSection }) {
  const { pathname } = useLocation();
  const subItems = group.items ?? [];
  const subActiveStates = subItems.map((sub) => isMatch(sub.to, pathname));
  const groupOwnActive = isMatch(group.to, pathname);
  const groupActive = groupOwnActive || subActiveStates.some(Boolean);

  return (
    <Collapsible defaultOpen={group.defaultOpen ?? true} render={<SidebarMenuItem />}>
      <SidebarMenuButton
        tooltip={group.title}
        isActive={groupActive}
        className="data-active:bg-transparent"
        render={group.to ? <Link to={group.to} /> : <button type="button" />}
      >
        {group.icon}
        {group.title ? <span>{group.title}</span> : null}
      </SidebarMenuButton>
      {subItems.length ? (
        <>
          <CollapsibleTrigger render={<SidebarMenuAction className="aria-expanded:rotate-90" />}>
            <ChevronRightIcon />
            <span className="sr-only">Toggle</span>
          </CollapsibleTrigger>
          <CollapsibleContent>
            <SidebarMenuSub>
              {subItems.map((sub, index) => (
                <SidebarMenuSubItem key={sub.to}>
                  <SidebarMenuSubButton isActive={subActiveStates[index]} render={<Link to={sub.to} />}>
                    <span>{sub.label}</span>
                    {sub.badge != null && <span className="ml-auto flex shrink-0">{sub.badge}</span>}
                  </SidebarMenuSubButton>
                </SidebarMenuSubItem>
              ))}
            </SidebarMenuSub>
          </CollapsibleContent>
        </>
      ) : null}
    </Collapsible>
  );
}

export function TenkoNavMain({ sections }: { sections: TenkoNavMainSection[] }) {
  return (
    <SidebarGroup>
      <SidebarMenu>
        {sections.map((group) => (
          <TenkoNavMainGroupEntry key={group.id} group={group} />
        ))}
      </SidebarMenu>
    </SidebarGroup>
  );
}
